/**
 * Apps-side glue for the tactics-hub — bridges Brain drafts to the
 * existing tactics-board persistence (`tactics3dStore.saveTacticsLayout`).
 *
 * Three Phase 1/2 draft shapes flow through `commitTacticsDraft`:
 *   - draft_tactics_pattern  (Phase 1) — full v=1 snapshot
 *   - draft_tactics_sequence (Phase 2) — multi-keyframe play + branches
 *   - draft_tactics_branch   (Phase 2) — single branch merged into the
 *                                        existing layout
 *
 * Phase 4 layers on top: a `save_as_pattern` instruction on any of
 * the three shapes also writes a `TacticsPattern` row into the
 * library after the live-board save.
 */
import { useCallback } from 'react';
import type {
  TacticsArrow,
  TacticsBranch,
  TacticsKeyframe,
  TacticsLayout,
  TacticsPlayerSpot,
} from '@4cm/core/tactics';
import { applyTacticsDraft } from '@4cm/brain/tactics/tacticsTools';
import {
  applyAnnotationDraft,
  type TacticsAnnotationDraftPayload,
} from './tacticsAnnotationsApply';
import {
  clearTacticsLayout,
  loadTacticsLayout,
  saveTacticsLayout,
} from './tactics3dStore';
import {
  savePattern,
  type TacticsPattern,
  type TacticsPatternPhase,
  type TacticsPatternSource,
} from './tacticsPatternsStore';

// World coordinate caps — keep in sync with Phase 1's tacticsTools.
const X_LIMIT = 34;
const Z_LIMIT = 52.5;

/**
 * Phase 4 — optional "also save this draft as a library pattern"
 * instruction on a tactics draft payload. Additive: the live board
 * write still happens, so the coach sees the result on the pitch
 * AND the pattern appears in the drawer.
 *
 * `formation` overrides the draft's `home_formation` for the saved
 * row label only — useful when the coach wants a curated label
 * different from whatever the board currently shows. Omit to inherit.
 */
export interface SaveAsPatternInstruction {
  name: string;
  description?: string;
  tags?: string[];
  phaseOfPlay: TacticsPatternPhase;
  formation?: string;
  sourceType?: TacticsPatternSource;
  curatorName?: string;
  /** Optional stable id — when set, upserts an existing pattern in place. */
  pattern_id?: string;
}

// Wire-shape interfaces — match the Brain tool schemas. The commit
// path tightens these to the canonical Tactics* types after clamping.

interface BrainSpot {
  role?: string;
  x?: number;
  z?: number;
}
interface BrainArrow {
  points?: Array<{ x?: number; z?: number }>;
  color?: string;
  player_key?: string;
}
interface BrainKeyframe {
  id?: string;
  t?: number;
  label?: string;
  home_spots?: BrainSpot[];
  away_spots?: BrainSpot[];
  arrows?: BrainArrow[];
}
interface BrainBranch {
  id?: string;
  parent_keyframe_id?: string;
  trigger?: string;
  keyframes?: BrainKeyframe[];
}

export interface TacticsDraftPayload {
  persist_key?: string;
  home_formation?: string;
  away_formation?: string;
  home_spots?: BrainSpot[];
  away_spots?: BrainSpot[];
  arrows?: BrainArrow[];
  intent?: string;
  /**
   * Phase 2 — multi-keyframe sequence + branches. Optional on the
   * pattern shape; populated on sequence/branch drafts. The commit
   * handler dispatches on payload shape, so all three flow through
   * one entry point.
   */
  keyframes?: BrainKeyframe[];
  branches?: BrainBranch[];
  // Branch-only drafts carry parent + trigger at the top level; the
  // branch's own keyframes live in the `keyframes` field above.
  id?: string;
  parent_keyframe_id?: string;
  trigger?: string;
  /**
   * Phase 4 — when present, the saved layout is also written into the
   * pattern library via `savePattern`. Additive to the live-board write.
   */
  save_as_pattern?: SaveAsPatternInstruction;
}

export interface TacticsCommitOk {
  ok: true;
  /** Saved TacticsLayout (post-clamp + merge with existing away spots). */
  layout: TacticsLayout;
  /** Resolved persistKey used (from draft.persist_key or the fallback). */
  persistKey: string;
  /** Phase 4 — populated when `save_as_pattern` was on the draft. */
  pattern?: TacticsPattern;
}
export interface TacticsCommitErr {
  ok: false;
  error: string;
}
export type TacticsCommitResult = TacticsCommitOk | TacticsCommitErr;

// ---------------------------------------------------------------------------
// Phase 2 helpers — sequence + branch normalisation. Clamps coords on
// entry so AI-hallucinated values can't escape onto the pitch.
// ---------------------------------------------------------------------------

function clampX(v: number): number {
  return Math.max(-X_LIMIT, Math.min(X_LIMIT, v));
}
function clampZ(v: number): number {
  return Math.max(-Z_LIMIT, Math.min(Z_LIMIT, v));
}

function normaliseSpot(input: BrainSpot | undefined): TacticsPlayerSpot | null {
  if (
    !input ||
    typeof input.role !== 'string' ||
    typeof input.x !== 'number' ||
    typeof input.z !== 'number'
  ) {
    return null;
  }
  return { role: input.role, x: clampX(input.x), y: 0.6, z: clampZ(input.z) };
}

function normaliseArrow(input: BrainArrow | undefined): TacticsArrow | null {
  if (!input?.points || !Array.isArray(input.points)) return null;
  const points = input.points
    .filter(
      (p): p is { x: number; z: number } =>
        typeof p?.x === 'number' && typeof p?.z === 'number',
    )
    .map((p) => ({ x: clampX(p.x), z: clampZ(p.z) }));
  if (points.length < 2) return null;
  return {
    id: `brain-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
    points,
    color: typeof input.color === 'string' ? input.color : '#4ade80',
    playerKey: input.player_key,
  };
}

function normaliseKeyframe(
  input: BrainKeyframe | undefined,
): TacticsKeyframe | null {
  if (!input || typeof input.id !== 'string') return null;
  const t =
    typeof input.t === 'number' ? Math.max(0, Math.min(1, input.t)) : 0;
  const label =
    typeof input.label === 'string' && input.label.length > 0
      ? input.label
      : 'Moment';
  const homeSpots = (input.home_spots ?? [])
    .map(normaliseSpot)
    .filter((s): s is TacticsPlayerSpot => s !== null);
  const awaySpots = (input.away_spots ?? [])
    .map(normaliseSpot)
    .filter((s): s is TacticsPlayerSpot => s !== null);
  const arrows = (input.arrows ?? [])
    .map((a) => normaliseArrow(a))
    .filter((a): a is TacticsArrow => a !== null);
  return { id: input.id, t, label, homeSpots, awaySpots, arrows };
}

function normaliseBranch(input: BrainBranch | undefined): TacticsBranch | null {
  if (
    !input ||
    typeof input.id !== 'string' ||
    typeof input.parent_keyframe_id !== 'string' ||
    typeof input.trigger !== 'string'
  ) {
    return null;
  }
  const keyframes = (input.keyframes ?? [])
    .map(normaliseKeyframe)
    .filter((k): k is TacticsKeyframe => k !== null);
  if (keyframes.length === 0) return null;
  return {
    id: input.id,
    parentKeyframeId: input.parent_keyframe_id,
    trigger: input.trigger,
    keyframes,
  };
}

function isBranchDraft(payload: TacticsDraftPayload): boolean {
  return (
    typeof payload.parent_keyframe_id === 'string' &&
    Array.isArray(payload.keyframes)
  );
}

function isSequenceDraft(payload: TacticsDraftPayload): boolean {
  return Array.isArray(payload.keyframes) && !payload.parent_keyframe_id;
}

/** Build a layout from a sequence draft. The first keyframe's
 *  positions also seed the v=1 snapshot fields so legacy renderers
 *  not yet keyframe-aware still get a valid pitch at t=0. */
function applySequenceDraft(
  payload: TacticsDraftPayload,
  existing: TacticsLayout | null,
): TacticsLayout | null {
  const keyframes = (payload.keyframes ?? [])
    .map(normaliseKeyframe)
    .filter((k): k is TacticsKeyframe => k !== null)
    .sort((a, b) => a.t - b.t);
  if (keyframes.length === 0) return null;

  const branches = (payload.branches ?? [])
    .map(normaliseBranch)
    .filter((b): b is TacticsBranch => b !== null)
    // Drop branches whose parent isn't in the sequence — hallucinated
    // parent ids would otherwise produce dead chips on the HUD.
    .filter((b) => keyframes.some((k) => k.id === b.parentKeyframeId));

  const k0 = keyframes[0];
  return {
    v: 1, // saveTacticsLayout owns v + saved_at; left here for typecheck.
    homeFormation: payload.home_formation ?? existing?.homeFormation ?? '4-3-3',
    awayFormation: payload.away_formation ?? existing?.awayFormation ?? '4-3-3',
    homeSpots: k0.homeSpots.length > 0 ? k0.homeSpots : (existing?.homeSpots ?? []),
    awaySpots: k0.awaySpots.length > 0 ? k0.awaySpots : (existing?.awaySpots ?? []),
    arrows: k0.arrows,
    keyframes,
    branches: branches.length > 0 ? branches : undefined,
    saved_at: new Date().toISOString(),
  };
}

/** Merge a single branch draft into the existing layout's branches.
 *  Requires the parent keyframe to exist so the HUD chip resolves. */
function applyBranchDraft(
  payload: TacticsDraftPayload,
  existing: TacticsLayout | null,
): TacticsLayout | null {
  if (!existing) return null;
  const branch = normaliseBranch({
    id: payload.id,
    parent_keyframe_id: payload.parent_keyframe_id,
    trigger: payload.trigger,
    keyframes: payload.keyframes,
  });
  if (!branch) return null;
  const existingKeyframes = existing.keyframes ?? [];
  if (!existingKeyframes.some((k) => k.id === branch.parentKeyframeId)) {
    return null;
  }
  // Replace a branch of the same id (re-author flow); otherwise append.
  // Stable ids stop the HUD's chip selection surprise-swapping when
  // the coach iterates on the trigger text.
  const otherBranches = (existing.branches ?? []).filter(
    (b) => b.id !== branch.id,
  );
  return {
    ...existing,
    branches: [...otherBranches, branch],
    saved_at: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Public commit entry point
// ---------------------------------------------------------------------------

/**
 * Commit a tactics draft to localStorage. Auto-detects the payload
 * shape: legacy single-snapshot (Phase 1), multi-keyframe sequence,
 * or a single branch merged into the existing layout (Phase 2).
 * Optionally writes a Phase 4 pattern-library row when the draft
 * carries a `save_as_pattern` instruction.
 *
 * `fallbackPersistKey` is used when the draft omits its own
 * persist_key (the Brain knows the active board from screen-context
 * but the field is still optional in the tool schema). Empty drafts
 * return an error the surface can show to the coach.
 */
export function commitTacticsDraft(
  payload: TacticsDraftPayload,
  fallbackPersistKey: string | null,
): TacticsCommitResult {
  const persistKey = payload.persist_key ?? fallbackPersistKey;
  if (!persistKey) {
    return { ok: false, error: 'no active tactics board (missing persistKey)' };
  }
  const existing = loadTacticsLayout(persistKey);

  let next: TacticsLayout | null;
  if (isBranchDraft(payload)) {
    next = applyBranchDraft(payload, existing);
    if (!next) {
      return {
        ok: false,
        error:
          'branch needs an existing parent keyframe in the active layout',
      };
    }
  } else if (isSequenceDraft(payload)) {
    next = applySequenceDraft(payload, existing);
    if (!next) {
      return { ok: false, error: 'draft has no usable keyframes' };
    }
  } else {
    // Phase 1 path — full snapshot apply.
    next = applyTacticsDraft(payload, existing);
    if (!next) {
      return { ok: false, error: 'draft has no usable home spots' };
    }
  }

  // saveTacticsLayout owns `v` + `saved_at`. Pass through every Phase
  // 2+ field so the schema-versioned read path can pick them up and
  // pre-existing annotations stay attached on a sequence write.
  const saved = saveTacticsLayout(persistKey, {
    homeFormation: next.homeFormation,
    awayFormation: next.awayFormation,
    homeSpots: next.homeSpots,
    awaySpots: next.awaySpots,
    arrows: next.arrows,
    keyframes: next.keyframes,
    branches: next.branches,
    zones: next.zones ?? existing?.zones,
    coachingPoints: next.coachingPoints ?? existing?.coachingPoints,
    constraints: next.constraints ?? existing?.constraints,
    voiceovers: next.voiceovers ?? existing?.voiceovers,
  });

  // Phase 4 — optional pattern-library write. Additive (the live board
  // write above still happens), so the coach sees the layout on the
  // pitch AND the row appears in the patterns drawer.
  let pattern: TacticsPattern | undefined;
  if (payload.save_as_pattern) {
    const ins = payload.save_as_pattern;
    pattern = savePattern({
      id: ins.pattern_id,
      name: ins.name,
      description: ins.description,
      tags: ins.tags,
      phaseOfPlay: ins.phaseOfPlay,
      formation: ins.formation ?? saved.homeFormation,
      layout: saved,
      sourceType: ins.sourceType,
      curatorName: ins.curatorName,
    });
  }

  return { ok: true, layout: saved, persistKey, pattern };
}

/**
 * Commit a Phase 3 annotation draft (zones / coaching points /
 * constraints / voice-overs) to localStorage.
 *
 * Annotation drafts are additive — the existing layout's home/away
 * spots, arrows, keyframes, branches stay intact. Only the v=3 fields
 * are merged. If the board has no layout yet (no Phase 1 pattern has
 * been committed), we surface an error rather than writing a layout
 * with annotations but no positions.
 */
export function commitTacticsAnnotationDraft(
  payload: TacticsAnnotationDraftPayload,
  fallbackPersistKey: string | null,
): TacticsCommitResult {
  const persistKey = payload.persist_key ?? fallbackPersistKey;
  if (!persistKey) {
    return { ok: false, error: 'no active tactics board (missing persistKey)' };
  }
  const existing = loadTacticsLayout(persistKey);
  if (!existing) {
    return {
      ok: false,
      error:
        'annotations need an existing layout — commit a tactics pattern first, then add zones / notes / voice-overs.',
    };
  }
  const next = applyAnnotationDraft(payload, existing);
  if (!next) {
    return {
      ok: false,
      error:
        'annotation draft had no usable zones / points / constraints / voice-overs',
    };
  }
  // saveTacticsLayout owns `v` + `saved_at`. Pass through every Phase
  // 1-3 field so nothing the coach had drops on write.
  const saved = saveTacticsLayout(persistKey, {
    homeFormation: next.homeFormation,
    awayFormation: next.awayFormation,
    homeSpots: next.homeSpots,
    awaySpots: next.awaySpots,
    arrows: next.arrows,
    keyframes: next.keyframes,
    branches: next.branches,
    zones: next.zones,
    coachingPoints: next.coachingPoints,
    constraints: next.constraints,
    voiceovers: next.voiceovers,
  });
  return { ok: true, layout: saved, persistKey };
}

/**
 * Phase 5 — commit a `draft_player_briefs` payload from the Brain
 * tool dispatcher. Writes the briefs into the active layout's
 * `playerBriefs` slot and clears the stale flag. Pure client-side
 * (briefs are a localStorage concern); no coach auth needed.
 */
export interface PlayerBriefsDraftPayload {
  /** Which team to brief — defaults to 'home'. */
  side?: 'home' | 'away';
  /** Optional override of the active board's persistKey. */
  persist_key?: string;
  /** Generated briefs — order matches `homeSpots`/`awaySpots`. */
  briefs: import('@4cm/core/tactics').PlayerBrief[];
}

export function commitPlayerBriefsDraft(
  payload: PlayerBriefsDraftPayload,
  fallbackPersistKey: string | null,
): TacticsCommitResult {
  const persistKey = payload.persist_key ?? fallbackPersistKey;
  if (!persistKey) {
    return { ok: false, error: 'no active tactics board (missing persistKey)' };
  }
  const existing = loadTacticsLayout(persistKey);
  if (!existing) {
    return {
      ok: false,
      error:
        'briefs need an existing tactics layout — open the board, set a formation, then ask for briefs.',
    };
  }
  const side = payload.side === 'away' ? 'away' : 'home';
  if (side === 'away') {
    // v1 only persists home briefs. Reject away cleanly so the coach
    // sees a useful error rather than silently dropping the draft.
    return { ok: false, error: 'away briefs are not persisted in v1' };
  }
  const briefsMap: Record<string, import('@4cm/core/tactics').PlayerBrief> = {};
  for (const b of payload.briefs ?? []) {
    briefsMap[b.playerKey] = b;
  }
  const saved = saveTacticsLayout(persistKey, {
    homeFormation: existing.homeFormation,
    awayFormation: existing.awayFormation,
    homeSpots: existing.homeSpots,
    awaySpots: existing.awaySpots,
    arrows: existing.arrows,
    keyframes: existing.keyframes,
    branches: existing.branches,
    zones: existing.zones,
    coachingPoints: existing.coachingPoints,
    constraints: existing.constraints,
    voiceovers: existing.voiceovers,
    playerRoles: existing.playerRoles,
    teamTactics: existing.teamTactics,
    setPieces: existing.setPieces,
    drawings: existing.drawings,
    overlays: existing.overlays,
    playerBriefs: briefsMap as import('@4cm/core/tactics').TacticsLayout['playerBriefs'],
    playerBriefsStale: false,
  });
  return { ok: true, layout: saved, persistKey };
}

/**
 * Hook for tactics surfaces — exposes a `commitTacticsDraft` handler
 * pinned to a known persistKey. Pass through to `useBrain`'s
 * `onCommitTacticsDraft` option so the approval path knows how to
 * apply this draft type.
 *
 * Also exposes `reset()` for symmetry with the existing Reset chip
 * (clear the layout when a fresh start is wanted via the Brain).
 */
export function useTacticsCommitForBoard(persistKey: string | null) {
  const commit = useCallback(
    (payload: TacticsDraftPayload): TacticsCommitResult => {
      return commitTacticsDraft(payload, persistKey);
    },
    [persistKey],
  );
  const commitAnnotation = useCallback(
    (payload: TacticsAnnotationDraftPayload): TacticsCommitResult => {
      return commitTacticsAnnotationDraft(payload, persistKey);
    },
    [persistKey],
  );
  const commitBriefs = useCallback(
    (payload: PlayerBriefsDraftPayload): TacticsCommitResult => {
      return commitPlayerBriefsDraft(payload, persistKey);
    },
    [persistKey],
  );
  const reset = useCallback(() => {
    if (persistKey) clearTacticsLayout(persistKey);
  }, [persistKey]);
  return { commit, commitAnnotation, commitBriefs, reset };
}
